import React, { useState, useMemo, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { FaSearch, FaFileExcel } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import InstallmentModal from "../components/InstallmentModal";

function HFA() {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  // Filter states
  const [installmentFilter, setInstallmentFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Role check
  const isChairman = localStorage.getItem("main_role") === "mainchairman";

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("adminUser")
    ? JSON.parse(localStorage.getItem("adminUser")).token
    : null;

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await axios.get(`${API_URL}/chairman-hfa-dashboard.php`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          setDashboardData(response.data);
        }
      } catch (error) {
        console.error("Dashboard API Error:", error);
      }
    };

    const fetchBeneficiaries = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/chairman-hfa-list.php`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success && Array.isArray(response.data.data)) {
          setBeneficiaries(response.data.data);
        } else {
          setBeneficiaries([]);
        }
      } catch (error) {
        toast.error("Failed to fetch beneficiaries.");
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    fetchBeneficiaries();
  }, [API_URL, token]);

  const handleShowInstallmentModal = (row) => {
    setSelectedBeneficiary(row);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBeneficiary(null);
  };

  const handleAddInstallment = (
    beneficiaryId,
    installmentNumber,
    amount,
    date,
    remark
  ) => {
    console.log("Installment added:", {
      beneficiaryId,
      installmentNumber,
      amount,
      date,
      remark,
    });
    handleCloseModal();
  };

  const filteredBeneficiaries = useMemo(() => {
    return beneficiaries.filter((b) => {
      const matchesSearch = searchTerm
        ? b.Beneficiary_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.Beneficiary_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.Beneficiary_fathername.toLowerCase().includes(
          searchTerm.toLowerCase()
        )
        : true;

      const [inst, status] = installmentFilter.split("_");
      const instNumber = parseInt(inst);
      let matchesInstallment = true;
      if (installmentFilter !== "all") {
        const suffix =
          instNumber === 1
            ? "st"
            : instNumber === 2
              ? "nd"
              : instNumber === 3
                ? "rd"
                : "th";
        const dateKey = `${instNumber}${suffix}_installment_date`;
        const installmentDate = b[dateKey];
        if (status === "paid") {
          matchesInstallment = installmentDate && installmentDate.trim() !== "";
        } else if (status === "unpaid") {
          matchesInstallment = !installmentDate || installmentDate.trim() === "";
        }
      }

      let matchesDate = true;
      if (startDate && endDate) {
        matchesDate = false;
        for (let i = 1; i <= 5; i++) {
          const suffix =
            i === 1 ? "st" : i === 2 ? "nd" : i === 3 ? "rd" : "th";
          const dateKey = `${i}${suffix}_installment_date`;
          const instDate = b[dateKey];
          if (instDate && instDate >= startDate && instDate <= endDate) {
            matchesDate = true;
            break;
          }
        }
      }
      return matchesSearch && matchesInstallment && matchesDate;
    });
  }, [beneficiaries, searchTerm, installmentFilter, startDate, endDate]);

  const handleExport = () => {
    const dataToExport = filteredBeneficiaries.map((b) => ({
      "Beneficiary Code": b.Beneficiary_code,
      Name: b.Beneficiary_name,
      "Father Name": b.Beneficiary_fathername,
      "Ward No": b.ward_no,
      "1st Inst Amount": b["1st_installment_amount"] || "",
      "1st Inst Date": b["1st_installment_date"] || "",
      "2nd Inst Amount": b["2nd_installment_amount"] || "",
      "2nd Inst Date": b["2nd_installment_date"] || "",
      "3rd Inst Amount": b["3rd_installment_amount"] || "",
      "3rd Inst Date": b["3rd_installment_date"] || "",
      "4th Inst Amount": b["4th_installment_amount"] || "",
      "4th Inst Date": b["4th_installment_date"] || "",
      "5th Inst Amount": b["5th_installment_amount"] || "",
      "5th Inst Date": b["5th_installment_date"] || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Beneficiaries");
    XLSX.writeFile(workbook, "HFA_Beneficiary_Report.xlsx");
  };

  const renderInstallmentCell = (row, installmentNumber) => {
    const suffix =
      installmentNumber === 1
        ? "st"
        : installmentNumber === 2
          ? "nd"
          : installmentNumber === 3
            ? "rd"
            : "th";
    const amountKey = `${installmentNumber}${suffix}_installment_amount`;
    const dateKey = `${installmentNumber}${suffix}_installment_date`;
    const amount = row[amountKey];
    const date = row[dateKey];

    // Check if date exists and is not empty (this indicates installment was paid)
    const hasInstallment = date && date.trim() !== "";

    return hasInstallment ? (
      <div>
        <div style={{ fontWeight: "500" }}>₹{parseFloat(amount || 0).toLocaleString("en-IN")}</div>
        <small className="text-muted" style={{ fontSize: "0.75rem" }}>{date}</small>
      </div>
    ) : (
      <span className="text-muted">N/A</span>
    );
  };

  const columns = [
    {
      name: "Beneficiary Code",
      selector: (row) => row.Beneficiary_code,
      sortable: true,
      grow: 2,
    },
    {
      name: "Name",
      selector: (row) => row.Beneficiary_name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Father's Name",
      selector: (row) => row.Beneficiary_fathername,
      sortable: true,
      grow: 2,
    },
    {
      name: "Ward No",
      selector: (row) => row.ward_no,
      sortable: true,
      width: "100px",
    },
    {
      name: "1st Inst",
      cell: (row) => renderInstallmentCell(row, 1),
      center: true,
    },
    {
      name: "2nd Inst",
      cell: (row) => renderInstallmentCell(row, 2),
      center: true,
    },
    {
      name: "3rd Inst",
      cell: (row) => renderInstallmentCell(row, 3),
      center: true,
    },
    {
      name: "4th Inst",
      cell: (row) => renderInstallmentCell(row, 4),
      center: true,
    },
    {
      name: "5th Inst",
      cell: (row) => renderInstallmentCell(row, 5),
      center: true,
    },
  ];

  // Conditionally add the Actions column if not chairman
  if (!isChairman) {
    columns.push({
      name: "Actions",
      cell: (row) => (
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleShowInstallmentModal(row)}
        >
          Installment
        </Button>
      ),
    });
  }

  return (
    <>
      <Topbar />
      <div className="d-flex" style={{ minHeight: "calc(100vh - 56px)" }}>
        <Sidebar />
        <div className="flex-grow-1 p-3" style={{ background: "#f0f2f5" }}>
          <Card
            className="shadow-sm"
            style={{ border: "none", borderRadius: "18px" }}
          >
            <Card.Header
              as="h5"
              style={{
                background: "#fff",
                borderBottom: "1px solid #eee",
                padding: "20px",
              }}
            >
              HFA Department {isChairman && "- Chairman's View"}
            </Card.Header>
            <Card.Body>
              {/* Dashboard Summary Cards */}
              {dashboardData && (
                <Row className="mb-4 g-3">
                  <Col md={3}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <h6 className="text-muted mb-2">Total Beneficiaries</h6>
                        <h3 className="mb-0 text-primary">{dashboardData.total_beneficiaries.toLocaleString()}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <h6 className="text-muted mb-2">Your Beneficiaries</h6>
                        <h3 className="mb-0 text-success">{dashboardData.user_beneficiaries.toLocaleString()}</h3>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <h6 className="text-muted mb-2">Total Installments Paid</h6>
                        <h3 className="mb-0 text-info">
                          {Object.values(dashboardData.installments).reduce((sum, inst) => sum + inst.paid, 0).toLocaleString()}
                        </h3>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border-0 shadow-sm h-100">
                      <Card.Body>
                        <h6 className="text-muted mb-2">Total Unpaid Installments</h6>
                        <h3 className="mb-0 text-danger">
                          {Object.values(dashboardData.installments).reduce((sum, inst) => sum + inst.unpaid, 0).toLocaleString()}
                        </h3>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Installment Progress */}
              {dashboardData && (
                <>
                  <h5 className="mb-3">Installment Progress</h5>
                  <Row className="mb-4 g-3">
                    {[1, 2, 3, 4, 5].map((i) => {
                      const suffix = i === 1 ? "st" : i === 2 ? "nd" : i === 3 ? "rd" : "th";
                      const instKey = `${i}${suffix}_installment`;
                      const instData = dashboardData.installments[instKey];
                      const total = instData.paid + instData.unpaid;
                      const percentage = total > 0 ? ((instData.paid / total) * 100).toFixed(1) : 0;

                      return (
                        <Col md={4} lg={2} key={i}>
                          <Card className="border-0 shadow-sm h-100">
                            <Card.Body>
                              <h6 className="mb-2">{i}{suffix} Installment</h6>
                              <div className="progress mb-2" style={{ height: "8px" }}>
                                <div
                                  className="progress-bar bg-success"
                                  role="progressbar"
                                  style={{ width: `${percentage}%` }}
                                  aria-valuenow={percentage}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>
                              <small className="text-muted">
                                Paid: <strong>{instData.paid}</strong> | Pending: <strong>{instData.unpaid}</strong>
                              </small>
                              <div><small className="text-success fw-bold">{percentage}%</small></div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </>
              )}

              <Form className="p-3 border-bottom">
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Form.Label>Search Beneficiary</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Search by name, code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Filter by Installment</Form.Label>
                    <Form.Select
                      value={installmentFilter}
                      onChange={(e) => setInstallmentFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <option key={`paid-${i}`} value={`${i}_paid`}>
                          {i}
                          {i === 1
                            ? "st"
                            : i === 2
                              ? "nd"
                              : i === 3
                                ? "rd"
                                : "th"}{" "}
                          Inst. Paid
                        </option>
                      ))}
                      {[1, 2, 3, 4, 5].map((i) => (
                        <option key={`unpaid-${i}`} value={`${i}_unpaid`}>
                          {i}
                          {i === 1
                            ? "st"
                            : i === 2
                              ? "nd"
                              : i === 3
                                ? "rd"
                                : "th"}{" "}
                          Inst. Unpaid
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={1} className="d-flex align-items-end">
                    <Button
                      variant="success"
                      onClick={handleExport}
                      className="w-100"
                      title="Export to Excel"
                    >
                      <FaFileExcel />
                    </Button>
                  </Col>
                </Row>
              </Form>
              <DataTable
                columns={columns}
                data={filteredBeneficiaries}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
                highlightOnHover
                responsive
                progressPending={loading}
                progressComponent={
                  <div className="p-5 text-center">
                    <Spinner animation="border" />
                  </div>
                }
              />
            </Card.Body>
          </Card>
        </div>
      </div>

      {!isChairman && selectedBeneficiary && (
        <InstallmentModal
          show={showModal}
          handleClose={handleCloseModal}
          beneficiary={selectedBeneficiary}
          onAddInstallment={handleAddInstallment}
        />
      )}
    </>
  );
}

export default HFA;
